import React, { useState, useEffect } from 'react';
import { 
  FiFileText, 
  FiPlus, 
  FiSearch, 
  FiFilter, 
  FiEdit2, 
  FiTrash2, 
  FiDownload,
  FiUser,
  FiUserCheck,
  FiCalendar,
  FiX,
  FiSave,
  FiActivity,
  FiEdit,
  FiUpload
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import styles from './PacientesNew.module.css';

const Exames = () => {
  const [exames, setExames] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pacienteFilter, setPacienteFilter] = useState('');
  const [medicoFilter, setMedicoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showModal, setShowModal] = useState(false);
  const [editingExame, setEditingExame] = useState(null);
  const [formData, setFormData] = useState({
    paciente_id: '',
    medico_id: '',
    tipo_exame: '',
    data_solicitacao: '',
    data_realizacao: '',
    status: 'solicitado',
    resultado: '',
    observacoes: '',
    arquivo_resultado: null
  });

  useEffect(() => {
    fetchExames();
    fetchPacientes();
    fetchMedicos();
  }, []);

  const fetchExames = async () => {
    try {
      const response = await axios.get('/medical/exames');
      if (response.data.success) {
        setExames(response.data.data.exames || []);
      }
    } catch (error) {
      console.error('Erro ao carregar exames:', error);
      toast.error('Erro ao carregar exames');
    } finally {
      setLoading(false);
    }
  };

  const fetchPacientes = async () => {
    try {
      const response = await axios.get('/medical/pacientes');
      if (response.data.success) {
        setPacientes(response.data.data.pacientes || []);
      }
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    }
  };

  const fetchMedicos = async () => {
    try {
      const response = await axios.get('/medical/medicos');
      if (response.data.success) {
        setMedicos(response.data.data.medicos || []);
      }
    } catch (error) {
      console.error('Erro ao carregar médicos:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      paciente_id: '',
      medico_id: '',
      tipo_exame: '',
      data_solicitacao: '',
      data_realizacao: '',
      status: 'solicitado',
      resultado: '',
      observacoes: '',
      arquivo_resultado: null
    });
    setEditingExame(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'arquivo_resultado') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });

      if (editingExame) {
        const response = await axios.put(`/medical/exames/${editingExame.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          toast.success('Exame atualizado com sucesso!');
        }
      } else {
        const response = await axios.post('/medical/exames', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          toast.success('Exame cadastrado com sucesso!');
        }
      }
      
      setShowModal(false);
      resetForm();
      fetchExames();
    } catch (error) {
      console.error('Erro ao salvar exame:', error);
      toast.error(editingExame ? 'Erro ao atualizar exame' : 'Erro ao cadastrar exame');
    }
  };

  const handleEdit = (exame) => {
    setEditingExame(exame);
    setFormData({
      paciente_id: exame.paciente_id || '',
      medico_id: exame.medico_id || '',
      tipo_exame: exame.tipo_exame || '',
      data_solicitacao: exame.data_solicitacao ? exame.data_solicitacao.split('T')[0] : '',
      data_realizacao: exame.data_realizacao ? exame.data_realizacao.split('T')[0] : '',
      status: exame.status || 'solicitado',
      resultado: exame.resultado || '',
      observacoes: exame.observacoes || '',
      arquivo_resultado: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este exame?')) {
      try {
        const response = await axios.delete(`/medical/exames/${id}`);
        if (response.data.success) {
          toast.success('Exame excluído com sucesso!');
          fetchExames();
        }
      } catch (error) {
        console.error('Erro ao excluir exame:', error);
        toast.error('Erro ao excluir exame');
      }
    }
  };

  const filteredExames = exames.filter(exame => {
    const matchSearch = 
      (exame.paciente_nome && exame.paciente_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (exame.medico_nome && exame.medico_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (exame.tipo_exame && exame.tipo_exame.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchPaciente = !pacienteFilter || 
      (exame.paciente_nome && exame.paciente_nome.toLowerCase().includes(pacienteFilter.toLowerCase()));
    
    const matchMedico = !medicoFilter || 
      (exame.medico_nome && exame.medico_nome.toLowerCase().includes(medicoFilter.toLowerCase()));
    
    const matchStatus = statusFilter === 'todos' || exame.status === statusFilter;
    
    return matchSearch && matchPaciente && matchMedico && matchStatus;
  });

  const clearAllFilters = () => {
    setSearchTerm('');
    setPacienteFilter('');
    setMedicoFilter('');
    setStatusFilter('todos');
  };

  const hasActiveFilters = searchTerm || pacienteFilter || medicoFilter || statusFilter !== 'todos';

  const getStatusColor = (status) => {
    switch (status) {
      case 'solicitado': return 'warning';
      case 'agendado': return 'info';
      case 'realizado': return 'success';
      case 'cancelado': return 'error';
      default: return 'neutral';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'solicitado': return 'Solicitado';
      case 'agendado': return 'Agendado';
      case 'realizado': return 'Realizado';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>
            <FiFileText />
            Exames
          </h1>
          <p className={styles.subtitle}>
            Gerencie exames, resultados e histórico médico
          </p>
        </div>
        <button 
          className="formButton formButtonPrimary"
          onClick={() => setShowModal(true)}
        >
          <FiPlus />
          Novo Exame
        </button>
      </div>

      {/* Estatísticas */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiFileText />
          </div>
          <div className={styles.statInfo}>
            <h3>{exames.length}</h3>
            <p>Total de Exames</p>
            <small className={styles.statDetail}>
              {filteredExames.length !== exames.length ? 
                `${filteredExames.length} nos filtros atuais` : 
                'Todos os exames cadastrados'
              }
            </small>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiCalendar />
          </div>
          <div className={styles.statInfo}>
            <h3>{exames.filter(e => e.status === 'agendado').length}</h3>
            <p>Exames Agendados</p>
            <small className={styles.statDetail}>
              Prontos para realização
            </small>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiActivity />
          </div>
          <div className={styles.statInfo}>
            <h3>{exames.filter(e => e.status === 'realizado').length}</h3>
            <p>Exames Realizados</p>
            <small className={styles.statDetail}>
              Com resultados disponíveis
            </small>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <FiUpload />
          </div>
          <div className={styles.statInfo}>
            <h3>{exames.filter(e => e.status === 'solicitado').length}</h3>
            <p>Solicitações Pendentes</p>
            <small className={styles.statDetail}>
              Aguardando agendamento
            </small>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className={styles.filtersSection}>
        {/* Busca Geral */}
        <div className={styles.searchContainer}>
          <div className={styles.searchBox}>
            <FiSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por tipo de exame, observações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => setSearchTerm('')}
              >
                <FiX />
              </button>
            )}
          </div>
        </div>

        {/* Filtros Específicos */}
        <div className={styles.filterRow}>
          {/* Busca por Paciente */}
          <div className={styles.filterBox}>
            <FiUser className={styles.filterIcon} />
            <input
              type="text"
              placeholder="Filtrar por paciente..."
              value={pacienteFilter}
              onChange={(e) => setPacienteFilter(e.target.value)}
              className={styles.filterInput}
            />
            {pacienteFilter && (
              <button
                type="button"
                className={styles.clearFilter}
                onClick={() => setPacienteFilter('')}
              >
                <FiX />
              </button>
            )}
          </div>

          {/* Busca por Médico */}
          <div className={styles.filterBox}>
            <FiUserCheck className={styles.filterIcon} />
            <input
              type="text"
              placeholder="Filtrar por médico..."
              value={medicoFilter}
              onChange={(e) => setMedicoFilter(e.target.value)}
              className={styles.filterInput}
            />
            {medicoFilter && (
              <button
                type="button"
                className={styles.clearFilter}
                onClick={() => setMedicoFilter('')}
              >
                <FiX />
              </button>
            )}
          </div>

          {/* Filtro de Status */}
          <div className={styles.filterGroup}>
            <FiFilter className={styles.filterIcon} />
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className={styles.statusSelect}
            >
              <option value="todos">Todos os Status</option>
              <option value="solicitado">Solicitados</option>
              <option value="agendado">Agendados</option>
              <option value="realizado">Realizados</option>
              <option value="cancelado">Cancelados</option>
            </select>
          </div>

          {/* Botão Limpar Filtros */}
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearAllFilters}
              onClick={clearAllFilters}
            >
              <FiX />
              Limpar Filtros
            </button>
          )}
        </div>

        {/* Informações dos Resultados */}
        <div className={styles.filterInfo}>
          <span className={styles.filterResults}>
            {filteredExames.length} de {exames.length} exames
            {hasActiveFilters && ' (filtrado)'}
          </span>
        </div>
      </div>

      {/* Lista de exames */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Tipo de Exame</th>
              <th>Paciente</th>
              <th>Médico Solicitante</th>
              <th>Data Solicitação</th>
              <th>Data Realização</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredExames.length === 0 ? (
              <tr>
                <td colSpan="7" className={styles.emptyState}>
                  <FiFileText />
                  <p>Nenhum exame encontrado</p>
                  <small>Clique em "Novo Exame" para cadastrar</small>
                </td>
              </tr>
            ) : (
              filteredExames.map((exame) => (
                <tr key={exame.id}>
                  <td>
                    <div className={styles.examType}>
                      <FiFileText />
                      {exame.tipo_exame || 'Não informado'}
                    </div>
                  </td>
                  <td>
                    <div className={styles.patientInfo}>
                      <FiUser />
                      {exame.paciente_nome || 'Paciente não identificado'}
                    </div>
                  </td>
                  <td>
                    <div className={styles.doctorInfo}>
                      <FiUserCheck />
                      {exame.medico_nome || 'Médico não identificado'}
                    </div>
                  </td>
                  <td>
                    <div className={styles.dateInfo}>
                      <FiCalendar />
                      {exame.data_solicitacao ? 
                        new Date(exame.data_solicitacao).toLocaleDateString('pt-BR') : 
                        'Não informada'
                      }
                    </div>
                  </td>
                  <td>
                    {exame.data_realizacao ? 
                      new Date(exame.data_realizacao).toLocaleDateString('pt-BR') : 
                      '--'
                    }
                  </td>
                  <td>
                    <span className={`${styles.badge} ${styles[getStatusColor(exame.status)]}`}>
                      {getStatusText(exame.status)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.button} ${styles.small} ${styles.outline}`}
                        onClick={() => handleEdit(exame)}
                        title="Editar exame"
                      >
                        <FiEdit2 />
                      </button>
                      {exame.arquivo_resultado && (
                        <button
                          className={`${styles.button} ${styles.small} ${styles.success}`}
                          title="Baixar resultado"
                        >
                          <FiDownload />
                        </button>
                      )}
                      <button
                        className={`${styles.button} ${styles.small} ${styles.danger}`}
                        onClick={() => handleDelete(exame.id)}
                        title="Excluir exame"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingExame ? 'Editar Exame' : 'Novo Exame'}
              </h2>
              <button
                className={styles.closeButton}
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                <FiX />
              </button>
            </div>
            <div className={styles.modalBody}>
            <form onSubmit={handleSubmit} className="form">
              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="paciente_id">Paciente</label>
                    <div className="formInputWrapper">
                      <FiUser className="formInputIcon" />
                      <select
                        id="paciente_id"
                        name="paciente_id"
                        value={formData.paciente_id}
                        onChange={handleInputChange}
                        className="formSelect"
                        required
                      >
                        <option value="">Selecionar paciente</option>
                        {pacientes.map(paciente => (
                          <option key={paciente.id} value={paciente.id}>
                            {paciente.nome_completo}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="medico_id">Médico Solicitante</label>
                    <div className="formInputWrapper">
                      <FiUserCheck className="formInputIcon" />
                      <select
                        id="medico_id"
                        name="medico_id"
                        value={formData.medico_id}
                        onChange={handleInputChange}
                        className="formSelect"
                        required
                      >
                        <option value="">Selecionar médico</option>
                        {medicos.map(medico => (
                          <option key={medico.id} value={medico.id}>
                            {medico.nome_completo} - {medico.especialidade}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="tipo_exame">Tipo de Exame</label>
                    <div className="formInputWrapper">
                      <FiFileText className="formInputIcon" />
                      <select
                        id="tipo_exame"
                        name="tipo_exame"
                        value={formData.tipo_exame}
                        onChange={handleInputChange}
                        className="formSelect"
                        required
                      >
                        <option value="">Selecionar tipo</option>
                        <option value="Hemograma">Hemograma</option>
                        <option value="Glicemia">Glicemia</option>
                        <option value="Colesterol">Colesterol</option>
                        <option value="Raio-X">Raio-X</option>
                        <option value="Ultrassom">Ultrassom</option>
                        <option value="Tomografia">Tomografia</option>
                        <option value="Ressonância">Ressonância</option>
                        <option value="Eletrocardiograma">Eletrocardiograma</option>
                        <option value="Outro">Outro</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup required">
                    <label className="formLabel" htmlFor="data_solicitacao">Data da Solicitação</label>
                    <div className="formInputWrapper">
                      <FiCalendar className="formInputIcon" />
                      <input
                        type="date"
                        id="data_solicitacao"
                        name="data_solicitacao"
                        value={formData.data_solicitacao}
                        onChange={handleInputChange}
                        className="formInput"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="formRow">
                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel" htmlFor="data_realizacao">Data da Realização</label>
                    <div className="formInputWrapper">
                      <FiCalendar className="formInputIcon" />
                      <input
                        type="date"
                        id="data_realizacao"
                        name="data_realizacao"
                        value={formData.data_realizacao}
                        onChange={handleInputChange}
                        className="formInput"
                      />
                    </div>
                  </div>
                </div>

                <div className="formCol">
                  <div className="formGroup">
                    <label className="formLabel" htmlFor="status">Status</label>
                    <div className="formInputWrapper">
                      <FiActivity className="formInputIcon" />
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="formSelect"
                      >
                        <option value="solicitado">Solicitado</option>
                        <option value="agendado">Agendado</option>
                        <option value="realizado">Realizado</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel" htmlFor="resultado">Resultado</label>
                <div className="formInputWrapper">
                  <FiFileText className="formInputIcon" />
                  <textarea
                    id="resultado"
                    name="resultado"
                    value={formData.resultado}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Resultado do exame..."
                    className="formTextarea"
                  />
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel" htmlFor="observacoes">Observações</label>
                <div className="formInputWrapper">
                  <FiEdit className="formInputIcon" />
                  <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Observações adicionais..."
                    className="formTextarea"
                  />
                </div>
              </div>

              <div className="formGroup">
                <label className="formLabel" htmlFor="arquivo_resultado">Arquivo do Resultado</label>
                <div className="formInputWrapper">
                  <FiUpload className="formInputIcon" />
                  <input
                    type="file"
                    id="arquivo_resultado"
                    name="arquivo_resultado"
                    onChange={handleInputChange}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    className="formInput"
                  />
                </div>
                <small className="formHint">Formatos aceitos: PDF, JPG, PNG, DOC, DOCX</small>
              </div>

              <div className="formActions">
                <button
                  type="button"
                  className="formButton formButtonSecondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="formButton formButtonPrimary"
                >
                  <FiSave />
                  {editingExame ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Exames;